import { UploadCloud, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { getPublicImageUrl, supabase } from '../../lib/supabase';

import { logger } from '@/lib/sentry';

interface ImageInputProps {
  value?: string | null;
  onChange: (value: string | null) => void;
  bucket: string;
}

export function ImageInput({ value, onChange, bucket }: ImageInputProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (file: File) => {
    try {
      setIsUploading(true);
      setUploadError(null);

      const fileName = `${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}-${Date.now()}`;

      const { data, error } = await supabase.storage.from(bucket).upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

      if (error) throw error;

      if (data) {
        onChange(data.path);
      }
    } catch (err) {
      logger.error(err, { context: 'imageUpload' });
      setUploadError((err as Error).message || 'Erreur lors du téléchargement');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file) {
        await uploadFile(file);
      }
    }
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onChange(urlInput.trim());
      setUrlInput('');
      setShowUrlInput(false);
    }
  };

  const currentPreviewUrl = getPublicImageUrl(bucket, value);

  return (
    <div className="space-y-3">
      {/* Current Image Preview */}
      {currentPreviewUrl ? (
        <div className="relative block w-full overflow-hidden rounded-lg border border-slate-200">
          <img
            src={currentPreviewUrl}
            alt="Preview"
            className="h-48 w-full bg-slate-100 object-contain"
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute right-2 top-2 rounded-full bg-white/80 p-1.5 text-slate-700 shadow-sm backdrop-blur-sm hover:bg-red-50 hover:text-red-600"
          >
            <X size={16} />
          </button>
        </div>
      ) : null}

      {!currentPreviewUrl && (
        <div className="w-full flex flex-col gap-3">
          {/* File Input */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 transition-colors
              ${isDragActive ? 'border-green-500 bg-green-50' : 'border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100'}
              ${isUploading ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpeg,.jpg,.png,.webp,image/*"
              className="hidden"
              onChange={(e) => void handleFileChange(e)}
            />
            <UploadCloud className="mb-2 text-slate-400" size={32} />
            {isUploading ? (
              <p className="text-sm font-medium text-slate-600">Téléchargement en cours...</p>
            ) : (
              <div className="text-center">
                <p className="text-sm font-medium text-slate-700">
                  Cliquez pour sélectionner une image
                </p>
                <p className="mt-1 text-xs text-slate-500">PNG, JPG, WEBP jusqu'à 5MB</p>
              </div>
            )}
          </div>

          {uploadError && <p className="text-sm text-red-500">{uploadError}</p>}

          {/* URL Input Toggle */}
          {!showUrlInput ? (
            <button
              type="button"
              onClick={() => setShowUrlInput(true)}
              className="text-left text-sm font-medium text-green-600 hover:text-green-700 hover:underline"
            >
              Ou saisir une URL (lien externe)
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleUrlSubmit())}
                placeholder="https://example.com/image.jpg"
                className="h-10 flex-1 rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleUrlSubmit}
                className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900"
              >
                Ajouter
              </button>
              <button
                type="button"
                onClick={() => setShowUrlInput(false)}
                className="rounded-lg px-2 py-2 text-slate-500 hover:bg-slate-100"
              >
                <X size={20} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
